import { Loader2, Sparkles, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { brandService } from "@/lib/services";
import type { Brand, BrandExtraction } from "@/types";

const EXTRACT_ACCEPT =
  "application/pdf,.docx,.pptx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation";

interface ExtractPanelProps {
  /** The brand being edited (edit mode enables "use candidate as logo"). */
  brand: Brand | null;
  /** Apply an extracted draft to the form (identity fields preserved). */
  onExtracted: (draft: BrandExtraction) => void;
  /** Set a logo candidate as the brand logo (edit mode only). */
  onUseLogoCandidate: (dataUri: string) => Promise<void>;
}

/** AI extraction panel: upload a guidelines doc, fill the form, surface logo candidates. */
export function ExtractPanel({ brand, onExtracted, onUseLogoCandidate }: Readonly<ExtractPanelProps>) {
  const { showToast } = useToast();
  const isEdit = brand !== null;
  const [extracting, setExtracting] = useState(false);
  const [logoCandidates, setLogoCandidates] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleExtract = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setExtracting(true);
    try {
      const res = await brandService.extract(file);
      const draft = res.data;
      onExtracted(draft);
      setLogoCandidates(draft.logoCandidates ?? []);
      showToast("Extracted a draft brand — review and adjust before saving", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err, "Could not extract a brand from that document"), "error");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border-secondary bg-surface-secondary/50 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-content-primary flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-brand-primary shrink-0" />
            Extract from document
          </p>
          <p className="text-xs text-content-tertiary mt-0.5">
            Upload brand guidelines (PDF, DOCX, PPTX) and AI will fill this form.
          </p>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={extracting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg text-sm font-medium border border-border-secondary transition shrink-0 disabled:opacity-60"
        >
          {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {extracting ? "Reading…" : "Choose file"}
        </button>
        <input ref={inputRef} type="file" accept={EXTRACT_ACCEPT} className="hidden" onChange={handleExtract} />
      </div>
      {logoCandidates.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-content-tertiary mb-1.5">
            Logo candidates found in the document
            {isEdit ? " — click one to use it" : " — usable after you create the brand"}:
          </p>
          <div className="flex flex-wrap gap-2">
            {logoCandidates.map((uri) =>
              isEdit ? (
                <button
                  key={uri.slice(-24)}
                  type="button"
                  onClick={() => onUseLogoCandidate(uri)}
                  title="Use as logo"
                  className="rounded border border-border-secondary bg-white p-1 hover:border-brand-primary transition cursor-pointer"
                >
                  <img src={uri} alt="Logo candidate" className="h-12 w-auto max-w-[120px] object-contain" />
                </button>
              ) : (
                <img
                  key={uri.slice(-24)}
                  src={uri}
                  alt="Logo candidate"
                  className="h-12 w-auto max-w-[120px] object-contain rounded border border-border-secondary bg-white p-1"
                />
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
